// App shell: auth gate, tactile Claymorphism chrome, and navigation of 04 §Global chrome.

import { lazy, Suspense, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { LoginPage } from '../features/auth/LoginPage.js';
import { signOut, useSession } from '../features/auth/useSession.js';
import { AdminPage } from '../features/admin/AdminPage.js';
import { useJobHealth } from '../features/admin/useAdmin.js';
import { CueCardPage } from '../features/leads/CueCardPage.js';
import { LeadsPage } from '../features/leads/LeadsPage.js';
import { KanbanPage } from '../features/sales/KanbanPage.js';
import { TodayPage } from '../features/today/TodayPage.js';
import { ClientsPage } from '../features/clients/ClientsPage.js';
import { LogsPage } from '../features/logs/LogsPage.js';
import { PipelinePage } from '../features/pipeline/PipelinePage.js';
import { useFollowUpsDue, useOpenReviewCount } from '../features/today/useToday.js';
import { isConfigured, isDemoMode } from '../lib/supabase.js';
import { CommandPalette } from '../components/CommandPalette.js';
import { NicheChip } from '../components/NicheChip.js';
import {
  IconAdmin,
  IconClients,
  IconDashboard,
  IconKanban,
  IconLeads,
  IconLogs,
  IconMap,
  IconMoon,
  IconPipeline,
  IconSearch,
  IconSun,
} from '../components/Icons.js';
import '../styles/tokens.css';
import '../styles/components.css';
import './app.css';
import { NotBuilt } from './NotBuilt.js';
import { ROUTES } from './routes.js';

const MapPage = lazy(async () => ({
  default: (await import('../features/map/MapPage.js')).MapPage,
}));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
});

function getNavIcon(path: string) {
  switch (path) {
    case '/':
      return <IconDashboard size={15} />;
    case '/leads':
      return <IconLeads size={15} />;
    case '/kanban':
      return <IconKanban size={15} />;
    case '/map':
      return <IconMap size={15} />;
    case '/clients':
      return <IconClients size={15} />;
    case '/pipeline':
      return <IconPipeline size={15} />;
    case '/logs':
      return <IconLogs size={15} />;
    case '/admin':
      return <IconAdmin size={15} />;
    default:
      return null;
  }
}

function PipelineStatusChip() {
  const { data: jobs = [] } = useJobHealth();
  const pipelineJob = jobs.find((j) => j.job_name === 'pipeline');
  const healthy =
    pipelineJob !== undefined &&
    pipelineJob.last_error === null &&
    pipelineJob.last_success_at !== null &&
    Date.now() - new Date(pipelineJob.last_success_at).getTime() <= 26 * 3600_000;

  return (
    <span
      className="shell__status"
      title={
        pipelineJob?.last_error ??
        (healthy ? 'Пайплайнът е в норма' : 'Пайплайнът няма скорошен успешен цикъл')
      }
    >
      <span
        className="shell__statusdot"
        style={healthy ? undefined : { background: 'var(--warn)' }}
      />
      <span className="shell__statustext">{healthy ? 'На живо' : 'Внимание'}</span>
    </span>
  );
}

function Chrome({
  email,
  theme,
  onToggleTheme,
  children,
}: {
  email: string;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  children: React.ReactNode;
}) {
  const [cmdOpen, setCmdOpen] = useState(false);
  const { data: followUps = [] } = useFollowUpsDue();
  const { data: openReviews = 0 } = useOpenReviewCount();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.key === 'k' && (event.metaKey || event.ctrlKey)) ||
        (event.key === '/' &&
          document.activeElement?.tagName !== 'INPUT' &&
          document.activeElement?.tagName !== 'TEXTAREA')
      ) {
        event.preventDefault();
        setCmdOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const activeRoutes = ROUTES.filter((r) => r.built);
  const upcomingRoutes = ROUTES.filter((r) => !r.built);

  return (
    <div className="shell">
      <header className="shell__bar">
        <div className="shell__brandgroup">
          <NavLink to="/" className="shell__brand">
            <span className="shell__logo-icon">G</span>
            <span>LeadG</span>
          </NavLink>
          <div className="shell__meta-pills">
            <NicheChip />
            <PipelineStatusChip />
            {isDemoMode && !isConfigured && (
              <span className="badge badge--warn shell__demobadge" title="Примерни локални данни">
                Демо режим
              </span>
            )}
          </div>
        </div>

        <nav className="shell__nav" aria-label="Основна навигация">
          {activeRoutes.map((route) => {
            const hasBadge =
              (route.path === '/' && followUps.length > 0) ||
              (route.path === '/admin' && openReviews > 0);
            const badgeCount = route.path === '/' ? followUps.length : openReviews;

            return (
              <NavLink
                key={route.path}
                to={route.path}
                end={route.path === '/'}
                className={({ isActive }) => `shell__tab${isActive ? ' shell__tab--on' : ''}`}
              >
                {getNavIcon(route.path)}
                <span>{route.labelBg}</span>
                {hasBadge && <span className="shell__tabbadge">{badgeCount}</span>}
              </NavLink>
            );
          })}

          {upcomingRoutes.length > 0 && <span className="shell__nav-sep" aria-hidden="true" />}

          {upcomingRoutes.map((route) => (
            <NavLink
              key={route.path}
              to={route.path}
              className={({ isActive }) =>
                `shell__tab shell__tab--soon${isActive ? ' shell__tab--on' : ''}`
              }
              title={`${route.labelBg} (предстои в пакет ${route.wp})`}
            >
              {getNavIcon(route.path)}
              <span>{route.labelBg}</span>
            </NavLink>
          ))}
        </nav>

        <div className="shell__actions">
          <button
            type="button"
            className="shell__searchbtn"
            onClick={() => setCmdOpen(true)}
            title="Бързо търсене (Ctrl+K или /)"
          >
            <IconSearch size={14} />
            <span className="shell__searchlabel">Търсене…</span>
            <kbd className="shell__kbd">/</kbd>
          </button>

          <button
            type="button"
            className="shell__themebtn"
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? 'Превключи на светъл режим' : 'Превключи на тъмен режим'}
            title={theme === 'dark' ? 'Превключи на светъл режим' : 'Превключи на тъмен режим'}
          >
            {theme === 'dark' ? <IconSun size={16} /> : <IconMoon size={16} />}
          </button>

          <div className="shell__user">
            <div className="shell__avatar" title={email}>
              {email.charAt(0).toUpperCase()}
            </div>
            <button
              type="button"
              className="shell__signout"
              onClick={() => void signOut()}
              title="Изход от профила"
            >
              Изход
            </button>
          </div>
        </div>
      </header>

      <main className="shell__body">{children}</main>

      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<TodayPage />} />
      <Route path="/leads" element={<LeadsPage />} />
      <Route path="/leads/:id" element={<LeadsPage />} />
      <Route path="/kanban" element={<KanbanPage />} />
      <Route
        path="/map"
        element={
          <Suspense fallback={<p className="boot">Зареждане на картата…</p>}>
            <MapPage />
          </Suspense>
        }
      />
      <Route path="/clients" element={<ClientsPage />} />
      <Route path="/clients/:id" element={<ClientsPage />} />
      <Route path="/pipeline" element={<PipelinePage />} />
      <Route path="/logs" element={<LogsPage />} />
      <Route path="/admin" element={<AdminPage />} />
      {ROUTES.filter((route) => !route.built).map((route) => (
        <Route key={route.path} path={route.path} element={<NotBuilt path={route.path} />} />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  if (
    typeof window !== 'undefined' &&
    window.history &&
    typeof window.history.replaceState === 'function' &&
    !(window.history as { __leadg_normalized?: boolean }).__leadg_normalized
  ) {
    const rawReplace = window.history.replaceState.bind(window.history);
    window.history.replaceState = (state: unknown, title: string, url?: string | URL | null) => {
      const target =
        !url || url === ''
          ? window.location.pathname
          : typeof url === 'string'
            ? url
            : url.toString();
      return rawReplace(state, title, target);
    };
    (window.history as { __leadg_normalized?: boolean }).__leadg_normalized = true;
  }

  const { session, loading } = useSession();
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    return (localStorage.getItem('leadg_theme') as 'dark' | 'light') || 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('leadg_theme', theme);
  }, [theme]);

  if (loading) return <div className="boot">Зареждане на LeadG…</div>;
  if (!session) return <LoginPage />;

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/leads/:id/cue" element={<CueCardPage />} />
          <Route
            path="*"
            element={
              <Chrome
                email={session.user.email ?? ''}
                theme={theme}
                onToggleTheme={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}
              >
                <AppRoutes />
              </Chrome>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
